import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { getAvailableSummary } from 'actions/applicants'
import { GROUP } from 'constants/reports/registry'
import { RAIL_GROUP, RAIL_GROUP_LABEL, ALERT, buildRailModel, getRunCount } from 'constants/reports/status'
import './reportsRail.scss'

const REPORT_GROUP_ORDER = [
    RAIL_GROUP.FLAGGED,
    RAIL_GROUP.CLEAR,
    RAIL_GROUP.NOT_RUN,
    RAIL_GROUP.UNKNOWN
]

const dotTone = (report) => {
    if (report.railGroup === RAIL_GROUP.UNKNOWN) return null
    if (report.railGroup === RAIL_GROUP.NOT_RUN) return 'grey'
    if (report.alertLevel === ALERT.ERROR) return 'red'
    if (report.railGroup === RAIL_GROUP.FLAGGED) return 'amber'
    return 'green'
}

const RailRow = ({ report, isActive, onSelect }) => {
    const tone = dotTone(report)

    return (
        <button
            type='button'
            className={`reportsRail__row${isActive ? ' reportsRail__row--active' : ''}`}
            onClick={() => onSelect(report.id)}
        >
            {tone && <span className={`reportsRail__dot reportsRail__dot--${tone}`} />}
            <span className='reportsRail__rowLabel'>{report.label}</span>
            {report.flaggedCount > 0 &&
                <span className='reportsRail__badge'>{report.flaggedCount}</span>
            }
        </button>
    )
}

const ReportsRail = (props) => {
    const { reports, activeReportId, onSelect, applicantId, updates } = props
    const [summaryEntries, setSummaryEntries] = useState([])
    const [query, setQuery] = useState('')

    useEffect(() => {
        if (!applicantId) return undefined

        let cancelled = false
        getAvailableSummary(applicantId)()
            .then((res) => {
                if (!cancelled) setSummaryEntries(Array.isArray(res) ? res : [])
            })
            .catch((error) => {
                console.error('Error fetching report statuses:', error)
                if (!cancelled) setSummaryEntries([])
            })

        return () => { cancelled = true }
    }, [applicantId, updates])

    const model = useMemo(() => buildRailModel(reports, summaryEntries), [reports, summaryEntries])
    const { run, total } = useMemo(() => getRunCount(model), [model])

    const visible = useMemo(() => {
        const term = query.trim().toLowerCase()
        return term ? model.filter(report => report.label.toLowerCase().includes(term)) : model
    }, [model, query])

    const pinned = visible.filter(report => report.group === GROUP.OVERVIEW)
    const workspace = visible.filter(report => report.group === GROUP.WORKSPACE)
    const reportRows = visible.filter(report => report.group === GROUP.REPORT)

    const renderRow = (report) => (
        <RailRow
            key={report.id}
            report={report}
            isActive={report.id === activeReportId}
            onSelect={onSelect}
        />
    )

    return (
        <nav className='reportsRail' aria-label='Reports'>
            <div className='reportsRail__header'>
                <span className='reportsRail__title'>Reports</span>
                {total > 0 &&
                    <span className='reportsRail__count'>{run} of {total} run</span>
                }
            </div>

            <input
                type='search'
                className='reportsRail__search'
                placeholder='Search reports'
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label='Search reports'
            />

            {pinned.length > 0 &&
                <div className='reportsRail__section'>{pinned.map(renderRow)}</div>
            }

            {REPORT_GROUP_ORDER.map((railGroup) => {
                const rows = reportRows.filter(report => report.railGroup === railGroup)
                if (!rows.length) return null

                return (
                    <div className='reportsRail__section' key={railGroup}>
                        <div className='reportsRail__sectionTitle'>{RAIL_GROUP_LABEL[railGroup]}</div>
                        {rows.map(renderRow)}
                    </div>
                )
            })}

            {workspace.length > 0 &&
                <div className='reportsRail__section'>
                    <div className='reportsRail__sectionTitle'>Workspace</div>
                    {workspace.map(renderRow)}
                </div>
            }

            {!visible.length &&
                <p className='reportsRail__empty'>No reports match “{query}”.</p>
            }
        </nav>
    )
}

ReportsRail.propTypes = {
    reports: PropTypes.array.isRequired,
    activeReportId: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    applicantId: PropTypes.string,
    updates: PropTypes.number
}

export default ReportsRail
